import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react';
import { setFormatLanguage } from './format';
import { STRINGS, type StringKey } from './strings';

export const LANGUAGES = ['ru', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<Language, string> = {
  ru: 'Русский',
  en: 'English',
};

const STORAGE_KEY = 'adbonus.admin.language';

/**
 * Russian unless this browser was told otherwise.
 *
 * Deliberately not taken from the browser's own language: the staff who use
 * this screen all day work the counter in Tbilisi and not all of them read
 * English, while the machine they sit at is as likely as not set up in English.
 * Guessing from it would hand them the one language they cannot work in.
 */
function detectLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ru' || saved === 'en') return saved;
  } catch {
    // a blocked storage is not a reason to fail to render
  }
  return 'ru';
}

/**
 * Picks the right noun form for a count.
 *
 * Russian needs three where English needs two — 1 машина, 2 машины, 5 машин —
 * so "{count} машин" printed "1 машин" on every single-item list. A string
 * writes the forms inline as `{count|машина|машины|машин}`; the language's own
 * entry supplies as many as that language needs, and the last one is used when
 * a language supplies fewer.
 */
function pickPlural(count: number, forms: string[], language: Language): string {
  const at = (index: number): string => forms[Math.min(index, forms.length - 1)] ?? '';
  if (language !== 'ru') return at(Math.abs(count) === 1 ? 0 : 1);

  const abs = Math.abs(count) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return at(2);
  if (last === 1) return at(0);
  if (last >= 2 && last <= 4) return at(1);
  return at(2);
}

interface I18nValue {
  language: Language;
  setLanguage: (next: Language) => void;
  t: (key: StringKey, params?: Record<string, unknown>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const initial = detectLanguage();
    setFormatLanguage(initial);
    return initial;
  });

  const setLanguage = useCallback((next: Language) => {
    // Before the re-render, so the dates in it come out in the new language.
    setFormatLanguage(next);
    setLanguageState(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // a preference we cannot persist is still worth honouring this session
    }
  }, []);

  const value = useMemo<I18nValue>(() => ({
    language,
    setLanguage,
    // `{name}` placeholders are filled from the caller. One that was not
    // supplied is left visible rather than printed as "undefined".
    t: (key, params) => {
      const text = STRINGS[key][language];
      if (!params) return text;
      // `{name}` is a plain substitution; `{name|one|few|many}` also agrees the
      // noun with the number, which Russian needs and English mostly does not.
      return text.replace(/\{(\w+)((?:\|[^}|]*)*)\}/g, (whole, name: string, tail: string) => {
        const value = params[name];
        if (value === undefined || value === null) return whole;
        if (!tail) return String(value);
        const forms = tail.slice(1).split('|');
        return `${value} ${pickPlural(Number(value), forms, language)}`;
      });
    },
  }), [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside I18nProvider');
  return value;
}

/**
 * Picks the reader's language out of a value the server stores in all three.
 * Service and category names are Localized in the database; showing the English
 * field to someone reading a Russian screen would leave half the page in a
 * language they came here to avoid.
 */
export function useLocalized(): (value: { ka: string; ru: string; en: string }) => string {
  const { language } = useI18n();
  return (value) => value[language] || value.en;
}

/**
 * The same value in the languages the reader is *not* currently seeing.
 *
 * Used for the small grey line under a service or reward name. Showing a fixed
 * pair there repeated the heading once the heading itself was localized.
 */
export function useOtherLanguages(): (value: { ka: string; ru: string; en: string }) => string {
  const { language } = useI18n();
  return (value) => (['ka', 'ru', 'en'] as const)
    .filter((code) => code !== language)
    .map((code) => value[code])
    .filter(Boolean)
    .join(' · ');
}

/** Whether a key exists, so a lookup does not have to rely on catching. */
export function hasString(key: string): key is StringKey {
  return Object.prototype.hasOwnProperty.call(STRINGS, key);
}

/**
 * Labels a code the database hands us — an order status, a payment method, a
 * language. Codes the schema gains later fall back to the code itself, tidied
 * up, so a new value reads as "partially refunded" instead of vanishing.
 */
export function useCodeLabel(): (prefix: string, code: string) => string {
  const { t } = useI18n();
  return (prefix, code) => {
    const key = `${prefix}.${code}`;
    return hasString(key) ? t(key) : code.replace(/_/g, ' ').toLowerCase();
  };
}

export type { StringKey };
